import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { Navbar, Nav, Container, Row, Col, Card, Button, Modal, Form, Alert, ProgressBar, Badge } from 'react-bootstrap';
import { useState, useEffect, useRef } from 'react';

// 图片常量
const aboutImage = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face';
const projectImage1 = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop';
const projectImage2 = 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=300&fit=crop';
const projectImage3 = 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=300&fit=crop';

// 安全的粒子背景组件
const ParticleBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    const particles = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const initParticles = () => {
      particles.length = 0;
      const particleCount = window.innerWidth < 768 ? 20 : 40;
      
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 3 + 1,
          opacity: Math.random() * 0.5 + 0.2
        });
      }
    };

    const animate = () => {
      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particles.forEach(particle => {
          particle.x += particle.vx;
          particle.y += particle.vy;

          if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
          if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(102, 126, 234, ${particle.opacity})`;
          ctx.fill();
        });

        animationFrameId = requestAnimationFrame(animate);
      } catch (error) {
        console.warn('Animation error:', error);
      }
    };

    resizeCanvas();
    initParticles();
    animate();

    const handleResize = () => {
      resizeCanvas();
      initParticles();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className="particle-background" />;
};

// 安全的打字机效果组件
const TypewriterEffect = ({ text = '', speed = 100 }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!text || currentIndex >= text.length) return;

    const timeout = setTimeout(() => {
      setDisplayText(prev => prev + text[currentIndex]);
      setCurrentIndex(prev => prev + 1);
    }, speed);

    return () => clearTimeout(timeout);
  }, [currentIndex, text, speed]);

  return (
    <span>
      {displayText}
      <span className="cursor">|</span>
    </span>
  );
};

// 安全的计数动画组件
const CountUpAnimation = ({ end = 0, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const startValue = 0;
    const endValue = parseInt(end) || 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * (endValue - startValue) + startValue);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// 滚动进度条组件
const ScrollProgress = () => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      try {
        const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
        setScrollProgress(Math.min(progress, 100));
      } catch (error) {
        console.warn('Scroll progress error:', error);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="scroll-progress">
      <div 
        className="scroll-progress-bar" 
        style={{ width: `${scrollProgress}%` }}
      />
    </div>
  );
};

// 增强的技能卡片组件
const SkillCard = ({ icon, title, description, skills }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className={`skill-card ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="skill-card-inner">
        <div className="skill-card-front">
          <div className="skill-icon">{icon}</div>
          <h5>{title}</h5>
          <p>{description}</p>
        </div>
        <div className="skill-card-back">
          <h6>Technologies:</h6>
          <div className="skill-tags">
            {skills.map((skill, index) => (
              <span key={index} className="skill-tag">{skill}</span>
            ))}
          </div>
          <p className="skill-back-desc">Click to explore more!</p>
        </div>
      </div>
    </div>
  );
};

// 项目过滤器组件
const ProjectFilter = ({ categories, activeFilter, onFilterChange }) => {
  return (
    <div className="project-filters">
      {categories.map(category => (
        <button
          key={category}
          className={`filter-btn ${activeFilter === category ? 'active' : ''}`}
          onClick={() => onFilterChange(category)}
        >
          {category}
        </button>
      ))}
    </div>
  );
};

// 增强的项目卡片组件
const ProjectCard = ({ project, onViewDetails }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Card className="project-card enhanced h-100">
      <div className="project-image-container">
        <Card.Img 
          variant="top" 
          src={project.image} 
          style={{height: '200px', objectFit: 'cover'}} 
          onLoad={() => setImageLoaded(true)}
          className={imageLoaded ? 'loaded' : 'loading'}
        />
        <div className="project-overlay">
          <div className="project-overlay-content">
            <Button 
              variant="light" 
              className="overlay-btn"
              onClick={() => window.open(project.liveUrl, '_blank')}
            >
              Live Demo
            </Button>
            <Button 
              variant="outline-light"
              className="overlay-btn"
              onClick={() => window.open(project.githubUrl, '_blank')}
            >
              View Code
            </Button>
          </div>
        </div>
      </div>
      <Card.Body className="d-flex flex-column">
        <div className="project-category-badge">
          <Badge bg="secondary">{project.category || 'Web Development'}</Badge>
        </div>
        <Card.Title className="project-title">{project.title}</Card.Title>
        <Card.Text className="flex-grow-1 project-description">{project.shortDesc}</Card.Text>
        <div className="project-technologies mb-3">
          {project.technologies.slice(0, 3).map((tech, index) => (
            <Badge key={index} bg="primary" className="me-1 mb-1 tech-badge">{tech}</Badge>
          ))}
          {project.technologies.length > 3 && (
            <Badge bg="light" text="dark" className="me-1 mb-1">+{project.technologies.length - 3}</Badge>
          )}
        </div>
        <Button className="project-button enhanced" onClick={() => onViewDetails(project)}>
          <span>View Details</span>
          <i className="arrow">→</i>
        </Button>
      </Card.Body>
    </Card>
  );
};

// 浮动操作按钮组件
const FloatingActionButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button 
      className={`floating-action-btn ${isVisible ? 'visible' : ''}`}
      onClick={scrollToTop}
      aria-label="Scroll to top"
    >
      ↑
    </button>
  );
};

// 几何图形动态背景
const GeometricBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const shapes = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createShapes = () => {
      shapes.length = 0;
      const shapeCount = window.innerWidth < 768 ? 15 : 25;

      for (let i = 0; i < shapeCount; i++) {
        shapes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 60 + 20,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 2,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          type: Math.floor(Math.random() * 3), // 0: triangle, 1: square, 2: circle
          opacity: Math.random() * 0.3 + 0.1,
          color: `hsl(${200 + Math.random() * 60}, 70%, 60%)`
        });
      }
    };

    const drawShape = (shape) => {
      ctx.save();
      ctx.translate(shape.x, shape.y);
      ctx.rotate((shape.rotation * Math.PI) / 180);
      ctx.globalAlpha = shape.opacity;
      ctx.fillStyle = shape.color;
      ctx.strokeStyle = shape.color;
      ctx.lineWidth = 2;

      switch (shape.type) {
        case 0: // Triangle
          ctx.beginPath();
          ctx.moveTo(0, -shape.size / 2);
          ctx.lineTo(-shape.size / 2, shape.size / 2);
          ctx.lineTo(shape.size / 2, shape.size / 2);
          ctx.closePath();
          ctx.stroke();
          break;
        case 1: // Square
          ctx.strokeRect(-shape.size / 2, -shape.size / 2, shape.size, shape.size);
          break;
        case 2: // Circle
          ctx.beginPath();
          ctx.arc(0, 0, shape.size / 2, 0, Math.PI * 2);
          ctx.stroke();
          break;
        default:
          break;
      }
      ctx.restore();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      shapes.forEach(shape => {
        shape.x += shape.vx;
        shape.y += shape.vy;
        shape.rotation += shape.rotationSpeed;

        if (shape.x < -50) shape.x = canvas.width + 50;
        if (shape.x > canvas.width + 50) shape.x = -50;
        if (shape.y < -50) shape.y = canvas.height + 50;
        if (shape.y > canvas.height + 50) shape.y = -50;

        drawShape(shape);
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createShapes();
    animate();

    const handleResize = () => {
      resizeCanvas();
      createShapes();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className={`geometric-background ${className}`} />;
};

// 波浪动态背景
const WaveBackground = ({ className = '', colors = ['#667eea', '#764ba2'] }) => {
  return (
    <div className={`wave-background ${className}`}>
      <svg className="wave-svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={colors[0]} stopOpacity="0.3" />
            <stop offset="100%" stopColor={colors[1]} stopOpacity="0.3" />
          </linearGradient>
        </defs>
        <path 
          d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
          fill="url(#waveGradient)" 
          className="wave-path wave-1"
        />
        <path 
          d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
          fill="url(#waveGradient)" 
          className="wave-path wave-2"
        />
        <path 
          d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
          fill="url(#waveGradient)" 
          className="wave-path wave-3"
        />
      </svg>
    </div>
  );
};

// 矩阵雨背景
const MatrixRainBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();

    const matrix = "ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789@#$%^&*()*&^%+-/~{[|`]}";
    const matrixArray = matrix.split("");
    const fontSize = 10;
    const columns = canvas.width / fontSize;
    const drops = [];

    for (let x = 0; x < columns; x++) {
      drops[x] = 1;
    }

    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#0F3';
      ctx.font = fontSize + 'px arial';

      for (let i = 0; i < drops.length; i++) {
        const text = matrixArray[Math.floor(Math.random() * matrixArray.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    const handleResize = () => {
      resizeCanvas();
      const newColumns = canvas.width / fontSize;
      drops.length = newColumns;
      for (let x = drops.length; x < newColumns; x++) {
        drops[x] = 1;
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className={`matrix-rain-background ${className}`} />;
};

// 星空背景
const StarfieldBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const stars = [];

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createStars = () => {
      stars.length = 0;
      const starCount = window.innerWidth < 768 ? 100 : 200;

      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          z: Math.random() * 1000,
          size: Math.random() * 2 + 1,
          speed: Math.random() * 2 + 0.5,
          opacity: Math.random() * 0.8 + 0.2
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      stars.forEach(star => {
        star.z -= star.speed;

        if (star.z <= 0) {
          star.x = Math.random() * canvas.width;
          star.y = Math.random() * canvas.height;
          star.z = 1000;
        }

        const x = (star.x - canvas.width / 2) * (1000 / star.z) + canvas.width / 2;
        const y = (star.y - canvas.height / 2) * (1000 / star.z) + canvas.height / 2;
        const size = (1 - star.z / 1000) * star.size;

        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * (1 - star.z / 1000)})`;
        ctx.fill();

        // Draw trail
        const prevX = (star.x - canvas.width / 2) * (1000 / (star.z + star.speed)) + canvas.width / 2;
        const prevY = (star.y - canvas.height / 2) * (1000 / (star.z + star.speed)) + canvas.height / 2;

        ctx.beginPath();
        ctx.moveTo(prevX, prevY);
        ctx.lineTo(x, y);
        ctx.strokeStyle = `rgba(255, 255, 255, ${star.opacity * 0.3 * (1 - star.z / 1000)})`;
        ctx.lineWidth = size;
        ctx.stroke();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    createStars();
    animate();

    const handleResize = () => {
      resizeCanvas();
      createStars();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className={`starfield-background ${className}`} />;
};

// 网格背景
const GridBackground = ({ className = '' }) => {
  return (
    <div className={`grid-background ${className}`}>
      <div className="grid-lines"></div>
      <div className="grid-dots"></div>
    </div>
  );
};

// 液体波动背景
const LiquidBackground = ({ className = '' }) => {
  return (
    <div className={`liquid-background ${className}`}>
      <div className="liquid-blob blob-1"></div>
      <div className="liquid-blob blob-2"></div>
      <div className="liquid-blob blob-3"></div>
      <div className="liquid-blob blob-4"></div>
    </div>
  );
};

// 动态内容背景系统
const DynamicContentBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);
  const contentElementsRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const clickEffectsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // 内容元素类型
    const ContentType = {
      FLOATING_TEXT: 'floating_text',
      SKILL_ICONS: 'skill_icons',
      PROJECT_CARDS: 'project_cards',
      TECH_BADGES: 'tech_badges',
      SOCIAL_ICONS: 'social_icons',
      GEOMETRIC_LOGOS: 'geometric_logos',
      CODE_SNIPPETS: 'code_snippets',
      DESIGN_ELEMENTS: 'design_elements'
    };

    // 预定义内容数据
    const contentData = {
      texts: [
        'React', 'JavaScript', 'TypeScript', 'Node.js', 'Python', 'CSS3', 'HTML5',
        'Vue.js', 'Angular', 'MongoDB', 'PostgreSQL', 'AWS', 'Docker', 'Git',
        'Full Stack', 'Frontend', 'Backend', 'UI/UX', 'API', 'Database',
        'Creative', 'Innovative', 'Professional', 'Responsive', 'Modern'
      ],
      skillIcons: ['💻', '⚙️', '🗄️', '☁️', '🎨', '🚀', '⚡', '🔧', '📱', '🌐'],
      codeSnippets: [
        'const app = () => {}',
        'function render() {}',
        'npm install',
        'git commit -m',
        'async/await',
        'useState()',
        'useEffect()',
        'API.get()',
        'CSS Grid',
        'Flexbox'
      ],
      designElements: ['◆', '▲', '●', '■', '★', '♦', '▼', '◇', '△', '○']
    };

    // 创建动态内容元素
    const createContentElement = () => {
      const types = Object.values(ContentType);
      const type = types[Math.floor(Math.random() * types.length)];
      const x = Math.random() * canvas.width;
      const y = -50;

      const baseElement = {
        id: Math.random().toString(36).substr(2, 9),
        type,
        x,
        y,
        vx: (Math.random() - 0.5) * 2,
        vy: Math.random() * 3 + 1,
        life: 1.0,
        maxLife: Math.random() * 8 + 5,
        size: Math.random() * 40 + 20,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 3,
        color: `hsl(${Math.random() * 360}, 70%, 60%)`,
        opacity: Math.random() * 0.8 + 0.2,
        scale: 1,
        targetScale: 1
      };

      switch (type) {
        case ContentType.FLOATING_TEXT:
          return {
            ...baseElement,
            text: contentData.texts[Math.floor(Math.random() * contentData.texts.length)],
            fontSize: Math.random() * 20 + 16,
            fontWeight: Math.random() > 0.5 ? 'bold' : 'normal',
            glowIntensity: Math.random() * 20 + 10
          };

        case ContentType.SKILL_ICONS:
          return {
            ...baseElement,
            icon: contentData.skillIcons[Math.floor(Math.random() * contentData.skillIcons.length)],
            bounceAmplitude: Math.random() * 10 + 5,
            bounceSpeed: Math.random() * 0.05 + 0.02
          };

        case ContentType.PROJECT_CARDS:
          return {
            ...baseElement,
            width: Math.random() * 80 + 60,
            height: Math.random() * 60 + 40,
            borderRadius: Math.random() * 15 + 5,
            shadowBlur: Math.random() * 20 + 10,
            gradient: {
              start: `hsl(${Math.random() * 360}, 70%, 60%)`,
              end: `hsl(${(Math.random() * 360 + 60) % 360}, 70%, 40%)`
            }
          };

        case ContentType.TECH_BADGES:
          return {
            ...baseElement,
            text: contentData.texts[Math.floor(Math.random() * contentData.texts.length)],
            badgeColor: `hsl(${Math.random() * 360}, 80%, 50%)`,
            textColor: '#ffffff',
            padding: Math.random() * 10 + 8
          };

        case ContentType.CODE_SNIPPETS:
          return {
            ...baseElement,
            code: contentData.codeSnippets[Math.floor(Math.random() * contentData.codeSnippets.length)],
            fontSize: Math.random() * 8 + 12,
            monospace: true,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            textColor: '#00ff00'
          };

        case ContentType.DESIGN_ELEMENTS:
          return {
            ...baseElement,
            element: contentData.designElements[Math.floor(Math.random() * contentData.designElements.length)],
            morphSpeed: Math.random() * 0.02 + 0.01,
            colorShift: Math.random() * 0.5 + 0.2
          };

        default:
          return baseElement;
      }
    };

    // 绘制内容元素
    const drawContentElement = (element) => {
      ctx.save();
      ctx.translate(element.x, element.y);
      ctx.rotate((element.rotation * Math.PI) / 180);
      ctx.scale(element.scale, element.scale);
      ctx.globalAlpha = element.opacity * element.life;

      switch (element.type) {
        case ContentType.FLOATING_TEXT:
          drawFloatingText(element);
          break;
        case ContentType.SKILL_ICONS:
          drawSkillIcon(element);
          break;
        case ContentType.PROJECT_CARDS:
          drawProjectCard(element);
          break;
        case ContentType.TECH_BADGES:
          drawTechBadge(element);
          break;
        case ContentType.CODE_SNIPPETS:
          drawCodeSnippet(element);
          break;
        case ContentType.DESIGN_ELEMENTS:
          drawDesignElement(element);
          break;
        default:
          break;
      }

      ctx.restore();
    };

    // 绘制浮动文字
    const drawFloatingText = (element) => {
      ctx.font = `${element.fontWeight} ${element.fontSize}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // 文字发光效果
      ctx.shadowColor = element.color;
      ctx.shadowBlur = element.glowIntensity;
      ctx.fillStyle = element.color;
      ctx.fillText(element.text, 0, 0);
      
      // 重复绘制增强发光
      ctx.shadowBlur = element.glowIntensity / 2;
      ctx.fillText(element.text, 0, 0);
      
      ctx.shadowBlur = 0;
    };

    // 绘制技能图标
    const drawSkillIcon = (element) => {
      const bounce = Math.sin(Date.now() * element.bounceSpeed) * element.bounceAmplitude;
      ctx.translate(0, bounce);
      
      ctx.font = `${element.size}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // 图标阴影
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetY = 5;
      
      ctx.fillText(element.icon, 0, 0);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
    };

    // 绘制项目卡片
    const drawProjectCard = (element) => {
      // 创建渐变
      const gradient = ctx.createLinearGradient(
        -element.width/2, -element.height/2,
        element.width/2, element.height/2
      );
      gradient.addColorStop(0, element.gradient.start);
      gradient.addColorStop(1, element.gradient.end);
      
      // 卡片阴影
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = element.shadowBlur;
      ctx.shadowOffsetY = 8;
      
      // 绘制圆角矩形
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.roundRect(-element.width/2, -element.height/2, element.width, element.height, element.borderRadius);
      ctx.fill();
      
      // 高光效果
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.beginPath();
      ctx.roundRect(-element.width/2, -element.height/2, element.width, element.height/3, element.borderRadius);
      ctx.fill();
    };

    // 绘制技术徽章
    const drawTechBadge = (element) => {
      ctx.font = `bold 14px Arial, sans-serif`;
      const textWidth = ctx.measureText(element.text).width;
      const badgeWidth = textWidth + element.padding * 2;
      const badgeHeight = 24;
      
      // 徽章背景
      ctx.fillStyle = element.badgeColor;
      ctx.beginPath();
      ctx.roundRect(-badgeWidth/2, -badgeHeight/2, badgeWidth, badgeHeight, 12);
      ctx.fill();
      
      // 徽章文字
      ctx.fillStyle = element.textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(element.text, 0, 0);
    };

    // 绘制代码片段
    const drawCodeSnippet = (element) => {
      ctx.font = `${element.fontSize}px 'Courier New', monospace`;
      const textWidth = ctx.measureText(element.code).width;
      const padding = 12;
      
      // 代码背景
      ctx.fillStyle = element.backgroundColor;
      ctx.beginPath();
      ctx.roundRect(-textWidth/2 - padding, -element.fontSize/2 - padding/2, 
                   textWidth + padding*2, element.fontSize + padding, 4);
      ctx.fill();
      
      // 代码文字
      ctx.fillStyle = element.textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(element.code, 0, 0);
    };

    // 绘制设计元素
    const drawDesignElement = (element) => {
      const time = Date.now() * element.morphSpeed;
      const colorShift = Math.sin(time) * element.colorShift;
      const hue = (parseFloat(element.color.match(/\d+/)[0]) + colorShift * 60) % 360;
      
      ctx.font = `${element.size}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = `hsl(${hue}, 70%, 60%)`;
      
      // 旋转变形效果
      const morphRotation = Math.sin(time * 2) * 0.2;
      ctx.rotate(morphRotation);
      
      ctx.fillText(element.element, 0, 0);
    };

    // 创建点击内容爆炸
    const createContentExplosion = (x, y) => {
      const explosionElements = [];
      
      // 创建文字爆炸
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const speed = Math.random() * 8 + 4;
        explosionElements.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          text: contentData.texts[Math.floor(Math.random() * contentData.texts.length)],
          life: 1.0,
          decay: Math.random() * 0.02 + 0.01,
          size: Math.random() * 16 + 12,
          color: `hsl(${Math.random() * 360}, 70%, 60%)`,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 10
        });
      }
      
      clickEffectsRef.current.push(...explosionElements);
    };

    // 更新内容元素
    const updateContentElements = () => {
      contentElementsRef.current.forEach(element => {
        // 基础运动
        element.x += element.vx;
        element.y += element.vy;
        element.rotation += element.rotationSpeed;
        element.life -= 1 / (element.maxLife * 60);

        // 鼠标交互
        const dx = mouseRef.current.x - element.x;
        const dy = mouseRef.current.y - element.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 150) {
          const force = (150 - distance) / 150 * 0.3;
          element.vx += (dx / distance) * force;
          element.vy += (dy / distance) * force;
          element.targetScale = 1 + force;
        } else {
          element.targetScale = 1;
        }
        
        // 平滑缩放
        element.scale += (element.targetScale - element.scale) * 0.1;

        // 类型特定更新
        switch (element.type) {
          case ContentType.FLOATING_TEXT:
            element.glowIntensity = 10 + Math.sin(Date.now() * 0.005) * 5;
            break;
          case ContentType.PROJECT_CARDS:
            element.shadowBlur = 10 + Math.sin(Date.now() * 0.003) * 5;
            break;
          case ContentType.DESIGN_ELEMENTS:
            element.size = 20 + Math.sin(Date.now() * element.morphSpeed * 10) * 10;
            break;
          default:
            break;
        }

        // 边界反弹
        if (element.x < 0 || element.x > canvas.width) {
          element.vx *= -0.8;
        }
        if (element.y > canvas.height + 100) {
          element.y = -50;
          element.x = Math.random() * canvas.width;
        }
      });

      // 更新点击效果
      clickEffectsRef.current.forEach(effect => {
        effect.x += effect.vx;
        effect.y += effect.vy;
        effect.vx *= 0.95;
        effect.vy *= 0.95;
        effect.rotation += effect.rotationSpeed;
        effect.life -= effect.decay;
      });

      // 清理过期元素
      contentElementsRef.current = contentElementsRef.current.filter(
        element => element.life > 0
      );
      clickEffectsRef.current = clickEffectsRef.current.filter(
        effect => effect.life > 0
      );
    };

    // 绘制点击效果
    const drawClickEffects = () => {
      clickEffectsRef.current.forEach(effect => {
        ctx.save();
        ctx.translate(effect.x, effect.y);
        ctx.rotate((effect.rotation * Math.PI) / 180);
        ctx.globalAlpha = effect.life;
        
        ctx.font = `bold ${effect.size}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = effect.color;
        ctx.fillText(effect.text, 0, 0);
        
        ctx.restore();
      });
    };

    // 主动画循环
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 随机生成新内容元素
      if (Math.random() < 0.03) { // 3% 概率
        contentElementsRef.current.push(createContentElement());
      }

      updateContentElements();

      // 绘制所有元素
      contentElementsRef.current.forEach(drawContentElement);
      drawClickEffects();

      animationFrameId = requestAnimationFrame(animate);
    };

    // 事件监听
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      createContentExplosion(x, y);
      
      // 在点击位置生成内容元素
      for (let i = 0; i < 5; i++) {
        const newElement = createContentElement();
        newElement.x = x + (Math.random() - 0.5) * 200;
        newElement.y = y + (Math.random() - 0.5) * 200;
        contentElementsRef.current.push(newElement);
      }
    };

    const handleTouch = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0] || e.changedTouches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      
      createContentExplosion(x, y);
      
      // 触摸生成更多内容
      for (let i = 0; i < 8; i++) {
        const newElement = createContentElement();
        newElement.x = x + (Math.random() - 0.5) * 300;
        newElement.y = y + (Math.random() - 0.5) * 300;
        contentElementsRef.current.push(newElement);
      }
    };

    resizeCanvas();
    animate();

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouch);
    canvas.addEventListener('touchmove', handleTouch);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouch);
      canvas.removeEventListener('touchmove', handleTouch);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className={`dynamic-content-background ${className}`} />;
};

// 动态随机动图背景系统
const DynamicAnimatedBackground = ({ className = '' }) => {
  const canvasRef = useRef(null);
  const animationsRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });
  const clickEffectsRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    // 动图类型枚举
    const AnimationType = {
      FLOATING_SHAPES: 'floating_shapes',
      SPIRAL_PARTICLES: 'spiral_particles',
      WAVE_RIPPLES: 'wave_ripples',
      ENERGY_ORBS: 'energy_orbs',
      GEOMETRIC_RAIN: 'geometric_rain',
      PLASMA_BALLS: 'plasma_balls',
      NEON_TRAILS: 'neon_trails',
      BUBBLE_STREAM: 'bubble_stream'
    };

    // 创建随机动图元素
    const createRandomAnimation = () => {
      const types = Object.values(AnimationType);
      const type = types[Math.floor(Math.random() * types.length)];
      const x = Math.random() * canvas.width;
      const y = -50; // 从顶部开始

      const baseAnimation = {
        id: Math.random().toString(36).substr(2, 9),
        type,
        x,
        y,
        life: 1.0,
        maxLife: Math.random() * 5 + 3,
        speed: Math.random() * 2 + 0.5,
        size: Math.random() * 30 + 10,
        color: `hsl(${Math.random() * 360}, 70%, 60%)`,
        opacity: Math.random() * 0.8 + 0.2,
        rotation: 0,
        rotationSpeed: (Math.random() - 0.5) * 5
      };

      switch (type) {
        case AnimationType.FLOATING_SHAPES:
          return {
            ...baseAnimation,
            shape: Math.floor(Math.random() * 4), // 0: circle, 1: square, 2: triangle, 3: star
            oscillation: Math.random() * 2 + 1,
            oscillationSpeed: Math.random() * 0.02 + 0.01
          };

        case AnimationType.SPIRAL_PARTICLES:
          return {
            ...baseAnimation,
            spiralRadius: Math.random() * 50 + 20,
            spiralSpeed: Math.random() * 0.1 + 0.05,
            particles: Array.from({ length: 8 }, (_, i) => ({
              angle: (i / 8) * Math.PI * 2,
              distance: Math.random() * 30 + 10
            }))
          };

        case AnimationType.WAVE_RIPPLES:
          return {
            ...baseAnimation,
            ripples: Array.from({ length: 5 }, (_, i) => ({
              radius: i * 15,
              opacity: 1 - (i * 0.2)
            }))
          };

        case AnimationType.ENERGY_ORBS:
          return {
            ...baseAnimation,
            energy: 1.0,
            pulseSpeed: Math.random() * 0.1 + 0.05,
            electricArcs: Array.from({ length: 6 }, () => ({
              angle: Math.random() * Math.PI * 2,
              length: Math.random() * 40 + 20
            }))
          };

        case AnimationType.GEOMETRIC_RAIN:
          return {
            ...baseAnimation,
            sides: Math.floor(Math.random() * 6) + 3,
            trail: []
          };

        case AnimationType.PLASMA_BALLS:
          return {
            ...baseAnimation,
            plasma: {
              intensity: Math.random() * 0.5 + 0.5,
              frequency: Math.random() * 0.1 + 0.05
            }
          };

        case AnimationType.NEON_TRAILS:
          return {
            ...baseAnimation,
            trail: [],
            maxTrailLength: Math.floor(Math.random() * 20) + 10,
            neonGlow: Math.random() * 20 + 10
          };

        case AnimationType.BUBBLE_STREAM:
          return {
            ...baseAnimation,
            bubbles: Array.from({ length: Math.floor(Math.random() * 5) + 3 }, () => ({
              x: (Math.random() - 0.5) * 60,
              y: (Math.random() - 0.5) * 60,
              size: Math.random() * 15 + 5,
              speed: Math.random() * 2 + 1
            }))
          };

        default:
          return baseAnimation;
      }
    };

    // 绘制不同类型的动图
    const drawAnimation = (animation) => {
      ctx.save();
      ctx.translate(animation.x, animation.y);
      ctx.rotate(animation.rotation);
      ctx.globalAlpha = animation.opacity * animation.life;

      switch (animation.type) {
        case AnimationType.FLOATING_SHAPES:
          drawFloatingShape(animation);
          break;
        case AnimationType.SPIRAL_PARTICLES:
          drawSpiralParticles(animation);
          break;
        case AnimationType.WAVE_RIPPLES:
          drawWaveRipples(animation);
          break;
        case AnimationType.ENERGY_ORBS:
          drawEnergyOrb(animation);
          break;
        case AnimationType.GEOMETRIC_RAIN:
          drawGeometricRain(animation);
          break;
        case AnimationType.PLASMA_BALLS:
          drawPlasmaBall(animation);
          break;
        case AnimationType.NEON_TRAILS:
          drawNeonTrail(animation);
          break;
        case AnimationType.BUBBLE_STREAM:
          drawBubbleStream(animation);
          break;
        default:
          break;
      }

      ctx.restore();
    };

    // 绘制浮动图形
    const drawFloatingShape = (animation) => {
      const oscillation = Math.sin(Date.now() * animation.oscillationSpeed) * animation.oscillation;
      ctx.translate(oscillation, 0);
      
      ctx.fillStyle = animation.color;
      ctx.strokeStyle = animation.color;
      ctx.lineWidth = 2;

      switch (animation.shape) {
        case 0: // Circle
          ctx.beginPath();
          ctx.arc(0, 0, animation.size, 0, Math.PI * 2);
          ctx.fill();
          break;
        case 1: // Square
          ctx.fillRect(-animation.size/2, -animation.size/2, animation.size, animation.size);
          break;
        case 2: // Triangle
          ctx.beginPath();
          ctx.moveTo(0, -animation.size/2);
          ctx.lineTo(-animation.size/2, animation.size/2);
          ctx.lineTo(animation.size/2, animation.size/2);
          ctx.closePath();
          ctx.fill();
          break;
        case 3: // Star
          drawStar(0, 0, 5, animation.size/2, animation.size/4);
          ctx.fill();
          break;
        default:
          break;
      }
    };

    // 绘制螺旋粒子
    const drawSpiralParticles = (animation) => {
      animation.particles.forEach((particle, index) => {
        const angle = particle.angle + Date.now() * animation.spiralSpeed;
        const x = Math.cos(angle) * particle.distance;
        const y = Math.sin(angle) * particle.distance;
        
        ctx.fillStyle = `hsl(${(Date.now() * 0.1 + index * 45) % 360}, 70%, 60%)`;
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    // 绘制波纹
    const drawWaveRipples = (animation) => {
      animation.ripples.forEach(ripple => {
        ctx.strokeStyle = animation.color;
        ctx.globalAlpha = ripple.opacity * animation.life;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, ripple.radius, 0, Math.PI * 2);
        ctx.stroke();
      });
    };

    // 绘制能量球
    const drawEnergyOrb = (animation) => {
      // 主球体
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, animation.size);
      gradient.addColorStop(0, animation.color);
      gradient.addColorStop(1, 'transparent');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, animation.size, 0, Math.PI * 2);
      ctx.fill();

      // 电弧
      animation.electricArcs.forEach(arc => {
        const endX = Math.cos(arc.angle) * arc.length;
        const endY = Math.sin(arc.angle) * arc.length;
        
        ctx.strokeStyle = animation.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      });
    };

    // 绘制几何雨
    const drawGeometricRain = (animation) => {
      // 绘制多边形
      ctx.fillStyle = animation.color;
      ctx.beginPath();
      for (let i = 0; i < animation.sides; i++) {
        const angle = (i / animation.sides) * Math.PI * 2;
        const x = Math.cos(angle) * animation.size;
        const y = Math.sin(angle) * animation.size;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();

      // 绘制轨迹
      if (animation.trail.length > 1) {
        ctx.strokeStyle = animation.color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        animation.trail.forEach((point, index) => {
          if (index === 0) ctx.moveTo(point.x - animation.x, point.y - animation.y);
          else ctx.lineTo(point.x - animation.x, point.y - animation.y);
        });
        ctx.stroke();
      }
    };

    // 绘制等离子球
    const drawPlasmaBall = (animation) => {
      const time = Date.now() * animation.plasma.frequency;
      const intensity = animation.plasma.intensity;
      
      for (let i = 0; i < 3; i++) {
        const radius = animation.size * (0.3 + i * 0.3);
        const alpha = intensity * (1 - i * 0.3) * Math.sin(time + i);
        
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius);
        gradient.addColorStop(0, `hsla(${(Date.now() * 0.1 + i * 120) % 360}, 70%, 60%, ${alpha})`);
        gradient.addColorStop(1, 'transparent');
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    // 绘制霓虹轨迹
    const drawNeonTrail = (animation) => {
      if (animation.trail.length > 1) {
        // 外发光
        ctx.shadowColor = animation.color;
        ctx.shadowBlur = animation.neonGlow;
        ctx.strokeStyle = animation.color;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        
        ctx.beginPath();
        animation.trail.forEach((point, index) => {
          const relX = point.x - animation.x;
          const relY = point.y - animation.y;
          if (index === 0) ctx.moveTo(relX, relY);
          else ctx.lineTo(relX, relY);
        });
        ctx.stroke();
        
        ctx.shadowBlur = 0;
      }
      
      // 主体
      ctx.fillStyle = animation.color;
      ctx.beginPath();
      ctx.arc(0, 0, animation.size/2, 0, Math.PI * 2);
      ctx.fill();
    };

    // 绘制气泡流
    const drawBubbleStream = (animation) => {
      animation.bubbles.forEach((bubble, index) => {
        const alpha = 0.6 * (1 - index * 0.1);
        ctx.fillStyle = `hsla(${(Date.now() * 0.05 + index * 60) % 360}, 70%, 70%, ${alpha})`;
        
        ctx.beginPath();
        ctx.arc(bubble.x, bubble.y, bubble.size, 0, Math.PI * 2);
        ctx.fill();
        
        // 气泡高光
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.5})`;
        ctx.beginPath();
        ctx.arc(bubble.x - bubble.size/3, bubble.y - bubble.size/3, bubble.size/4, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    // 绘制星形
    const drawStar = (cx, cy, spikes, outerRadius, innerRadius) => {
      let rot = Math.PI / 2 * 3;
      let x = cx;
      let y = cy;
      const step = Math.PI / spikes;

      ctx.beginPath();
      ctx.moveTo(cx, cy - outerRadius);
      
      for (let i = 0; i < spikes; i++) {
        x = cx + Math.cos(rot) * outerRadius;
        y = cy + Math.sin(rot) * outerRadius;
        ctx.lineTo(x, y);
        rot += step;

        x = cx + Math.cos(rot) * innerRadius;
        y = cy + Math.sin(rot) * innerRadius;
        ctx.lineTo(x, y);
        rot += step;
      }
      
      ctx.lineTo(cx, cy - outerRadius);
      ctx.closePath();
    };

    // 创建点击效果
    const createClickEffect = (x, y) => {
      const effects = [];
      
      // 爆炸粒子
      for (let i = 0; i < 15; i++) {
        effects.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 10,
          vy: (Math.random() - 0.5) * 10,
          life: 1.0,
          decay: Math.random() * 0.02 + 0.01,
          size: Math.random() * 8 + 2,
          color: `hsl(${Math.random() * 360}, 70%, 60%)`
        });
      }
      
      // 冲击波
      effects.push({
        x,
        y,
        radius: 0,
        maxRadius: 100,
        life: 1.0,
        decay: 0.03,
        type: 'shockwave'
      });
      
      clickEffectsRef.current.push(...effects);
    };

    // 更新动画
    const updateAnimations = () => {
      // 更新主动画
      animationsRef.current.forEach(animation => {
        animation.y += animation.speed;
        animation.rotation += animation.rotationSpeed;
        animation.life -= 1 / (animation.maxLife * 60); // 60fps

        // 更新特定类型的动画
        switch (animation.type) {
          case AnimationType.WAVE_RIPPLES:
            animation.ripples.forEach(ripple => {
              ripple.radius += 2;
              ripple.opacity *= 0.98;
            });
            break;
            
          case AnimationType.GEOMETRIC_RAIN:
            animation.trail.push({ x: animation.x, y: animation.y });
            if (animation.trail.length > 10) {
              animation.trail.shift();
            }
            break;
            
          case AnimationType.NEON_TRAILS:
            animation.trail.push({ x: animation.x, y: animation.y });
            if (animation.trail.length > animation.maxTrailLength) {
              animation.trail.shift();
            }
            break;
            
          case AnimationType.BUBBLE_STREAM:
            animation.bubbles.forEach(bubble => {
              bubble.y -= bubble.speed;
              bubble.x += Math.sin(Date.now() * 0.001 + bubble.y * 0.01) * 0.5;
            });
            break;
          default:
            break;
        }

        // 鼠标吸引效果
        const dx = mouseRef.current.x - animation.x;
        const dy = mouseRef.current.y - animation.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          const force = (100 - distance) / 100 * 0.5;
          animation.x += (dx / distance) * force;
          animation.y += (dy / distance) * force;
        }
      });

      // 更新点击效果
      clickEffectsRef.current.forEach(effect => {
        if (effect.type === 'shockwave') {
          effect.radius += (effect.maxRadius - effect.radius) * 0.1;
          effect.life -= effect.decay;
        } else {
          effect.x += effect.vx;
          effect.y += effect.vy;
          effect.vx *= 0.98;
          effect.vy *= 0.98;
          effect.life -= effect.decay;
        }
      });

      // 移除过期的动画和效果
      animationsRef.current = animationsRef.current.filter(
        animation => animation.life > 0 && animation.y < canvas.height + 100
      );
      
      clickEffectsRef.current = clickEffectsRef.current.filter(
        effect => effect.life > 0
      );
    };

    // 绘制点击效果
    const drawClickEffects = () => {
      clickEffectsRef.current.forEach(effect => {
        ctx.save();
        ctx.globalAlpha = effect.life;
        
        if (effect.type === 'shockwave') {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(effect.x, effect.y, effect.radius, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          ctx.fillStyle = effect.color;
          ctx.beginPath();
          ctx.arc(effect.x, effect.y, effect.size, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.restore();
      });
    };

    // 主动画循环
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 随机生成新动画
      if (Math.random() < 0.02) { // 2% 概率每帧
        animationsRef.current.push(createRandomAnimation());
      }

      updateAnimations();

      // 绘制所有动画
      animationsRef.current.forEach(drawAnimation);
      drawClickEffects();

      animationFrameId = requestAnimationFrame(animate);
    };

    // 事件监听
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    };

    const handleClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      createClickEffect(x, y);
      
      // 在点击位置生成额外的动画
      for (let i = 0; i < 3; i++) {
        const newAnimation = createRandomAnimation();
        newAnimation.x = x + (Math.random() - 0.5) * 100;
        newAnimation.y = y + (Math.random() - 0.5) * 100;
        animationsRef.current.push(newAnimation);
      }
    };

    const handleTouch = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0] || e.changedTouches[0];
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      
      createClickEffect(x, y);
      
      // 触摸反馈
      for (let i = 0; i < 5; i++) {
        const newAnimation = createRandomAnimation();
        newAnimation.x = x + (Math.random() - 0.5) * 150;
        newAnimation.y = y + (Math.random() - 0.5) * 150;
        animationsRef.current.push(newAnimation);
      }
    };

    resizeCanvas();
    animate();

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('click', handleClick);
    canvas.addEventListener('touchstart', handleTouch);
    canvas.addEventListener('touchmove', handleTouch);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('click', handleClick);
      canvas.removeEventListener('touchstart', handleTouch);
      canvas.removeEventListener('touchmove', handleTouch);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className={`dynamic-animated-background ${className}`} />;
};

// 主应用组件
function App() {
  // 状态管理
  const [showContactModal, setShowContactModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');

  // 页面加载效果
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // 项目数据
  const projects = [
    {
      id: 1,
      title: 'E-Commerce Platform',
      shortDesc: 'A full-stack e-commerce solution built with React, Node.js, and MongoDB.',
      fullDesc: 'A comprehensive e-commerce platform featuring user authentication, shopping cart functionality, payment processing with Stripe, real-time inventory management, admin dashboard, order tracking, and responsive design.',
      technologies: ['React', 'Node.js', 'MongoDB', 'Express', 'Stripe API', 'AWS'],
      features: ['User Authentication', 'Shopping Cart', 'Payment Processing', 'Admin Dashboard', 'Order Tracking', 'Inventory Management'],
      image: projectImage1,
      liveUrl: 'https://example-ecommerce.com',
      githubUrl: 'https://github.com/username/ecommerce-platform',
      category: 'Full Stack'
    },
    {
      id: 2,
      title: 'Task Management App',
      shortDesc: 'A collaborative task management application with real-time updates.',
      fullDesc: 'A modern task management application that enables teams to collaborate effectively. Features include drag-and-drop task boards, real-time updates using WebSocket, team member assignments, deadline tracking, file attachments, and progress analytics.',
      technologies: ['React', 'Firebase', 'Material-UI', 'WebSocket', 'Chart.js'],
      features: ['Drag & Drop Interface', 'Real-time Collaboration', 'Team Management', 'File Attachments', 'Progress Analytics', 'Deadline Notifications'],
      image: projectImage2,
      liveUrl: 'https://example-taskmanager.com',
      githubUrl: 'https://github.com/username/task-manager',
      category: 'Frontend'
    },
    {
      id: 3,
      title: 'Data Visualization Dashboard',
      shortDesc: 'An interactive dashboard for data visualization and analytics.',
      fullDesc: 'A powerful data visualization dashboard that transforms complex datasets into interactive charts and graphs. Features include customizable widgets, real-time data updates, export functionality, user role management, and responsive design.',
      technologies: ['React', 'D3.js', 'Chart.js', 'REST APIs', 'PostgreSQL'],
      features: ['Interactive Charts', 'Real-time Updates', 'Custom Widgets', 'Data Export', 'Role Management', 'API Integration'],
      image: projectImage3,
      liveUrl: 'https://example-dashboard.com',
      githubUrl: 'https://github.com/username/data-dashboard',
      category: 'Data Science'
    },
    {
      id: 4,
      title: 'Mobile Banking App',
      shortDesc: 'Secure mobile banking application with biometric authentication.',
      fullDesc: 'A comprehensive mobile banking solution featuring biometric authentication, real-time transactions, budget tracking, investment portfolio management, and AI-powered financial insights.',
      technologies: ['React Native', 'Node.js', 'PostgreSQL', 'Redis', 'JWT'],
      features: ['Biometric Auth', 'Real-time Transactions', 'Budget Tracking', 'Investment Management', 'AI Insights'],
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop',
      liveUrl: 'https://example-banking.com',
      githubUrl: 'https://github.com/username/banking-app',
      category: 'Mobile'
    },
    {
      id: 5,
      title: 'AI Content Generator',
      shortDesc: 'AI-powered content generation platform for marketers and writers.',
      fullDesc: 'An intelligent content generation platform that uses advanced AI models to create blog posts, social media content, product descriptions, and marketing copy with SEO optimization.',
      technologies: ['Python', 'OpenAI API', 'FastAPI', 'React', 'TensorFlow'],
      features: ['AI Content Generation', 'SEO Optimization', 'Multi-format Output', 'Plagiarism Check', 'Content Analytics'],
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop',
      liveUrl: 'https://example-ai-content.com',
      githubUrl: 'https://github.com/username/ai-content-generator',
      category: 'AI/ML'
    },
    {
      id: 6,
      title: 'Blockchain Voting System',
      shortDesc: 'Secure and transparent voting system built on blockchain technology.',
      fullDesc: 'A decentralized voting platform ensuring transparency, security, and immutability. Features smart contracts, voter verification, real-time results, and audit trails.',
      technologies: ['Solidity', 'Web3.js', 'Ethereum', 'React', 'IPFS'],
      features: ['Smart Contracts', 'Voter Verification', 'Real-time Results', 'Audit Trails', 'Decentralized Storage'],
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=400&h=300&fit=crop',
      liveUrl: 'https://example-voting.com',
      githubUrl: 'https://github.com/username/blockchain-voting',
      category: 'Blockchain'
    }
  ];

  // 项目类别
  const projectCategories = ['All', 'Full Stack', 'Frontend', 'Mobile', 'AI/ML', 'Blockchain', 'Data Science'];

  // 过滤项目
  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  // 技能卡片数据
  const skillCards = [
    {
      icon: '💻',
      title: 'Frontend Development',
      description: 'Modern UI/UX with cutting-edge technologies',
      skills: ['React', 'Vue.js', 'TypeScript', 'Next.js', 'Tailwind CSS']
    },
    {
      icon: '⚙️',
      title: 'Backend Development', 
      description: 'Scalable server solutions and APIs',
      skills: ['Node.js', 'Python', 'Express', 'Django', 'GraphQL']
    },
    {
      icon: '🗄️',
      title: 'Database Management',
      description: 'Efficient data storage and retrieval',
      skills: ['MongoDB', 'PostgreSQL', 'Redis', 'Firebase', 'MySQL']
    },
    {
      icon: '☁️',
      title: 'Cloud & DevOps',
      description: 'Deployment and infrastructure automation',
      skills: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Terraform']
    }
  ];

  // 技能数据
  const skills = [
    { name: 'React.js', level: 95, category: 'Frontend' },
    { name: 'JavaScript/TypeScript', level: 90, category: 'Frontend' },
    { name: 'Vue.js', level: 85, category: 'Frontend' },
    { name: 'HTML5/CSS3', level: 95, category: 'Frontend' },
    { name: 'Node.js', level: 88, category: 'Backend' },
    { name: 'Python', level: 82, category: 'Backend' },
    { name: 'Express.js', level: 85, category: 'Backend' },
    { name: 'Django', level: 78, category: 'Backend' }
  ];

  // 事件处理函数
  const handleContactSubmit = (e) => {
    e.preventDefault();
    try {
      console.log('Contact form submitted:', contactForm);
      setAlertMessage('Thank you for your message! I will get back to you soon.');
      setShowAlert(true);
      setShowContactModal(false);
      setContactForm({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => setShowAlert(false), 3000);
    } catch (error) {
      console.error('Contact form error:', error);
      setAlertMessage('Sorry, there was an error sending your message.');
      setShowAlert(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openProjectModal = (project) => {
    setSelectedProject(project);
    setShowProjectModal(true);
  };

  const handleDownloadResume = () => {
    setShowResumeModal(true);
  };

  const scrollToSection = (sectionId) => {
    try {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.warn('Scroll error:', error);
    }
  };

  // 加载屏幕
  if (isLoading) {
    return (
      <div className="loading-overlay">
        <div className="loading-content">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </div>
          <h3 className="loading-text">Loading Amazing Experience...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <DynamicContentBackground className="global-content-bg" />
      <DynamicAnimatedBackground className="global-animated-bg" />
      <ScrollProgress />
      <FloatingActionButton />
      {/* 导航栏 */}
      <Navbar bg="dark" variant="dark" expand="lg" sticky="top">
        <Container>
          <Navbar.Brand href="#home">My Portfolio</Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              <Nav.Link onClick={() => scrollToSection('home')}>Home</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('about')}>About</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('skills')}>Skills</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('portfolio')}>Portfolio</Nav.Link>
              <Nav.Link onClick={() => scrollToSection('contact')}>Contact</Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* 英雄区域 */}
      <header id="home" className="hero-section">
        <ParticleBackground />
        <Container>
          <div className="hero-content">
            <h1 className="hero-title">
              <TypewriterEffect text="Welcome to My Digital World" speed={80} />
            </h1>
            <p className="hero-subtitle">
              <TypewriterEffect text="Creative Developer & Designer" speed={60} />
            </p>
            <p className="lead mb-4">Crafting beautiful digital experiences with passion and precision</p>
            <div className="hero-buttons">
              <Button className="hero-button me-3" size="lg" onClick={() => scrollToSection('portfolio')}>
                Explore My Work
              </Button>
              <Button variant="outline-light" size="lg" onClick={() => setShowContactModal(true)}>
                Get In Touch
              </Button>
            </div>
            <div className="hero-stats mt-5">
              <Row className="text-center">
                <Col md={3} sm={6} className="mb-3">
                  <div className="stat-item">
                    <h3 className="stat-number">
                      <CountUpAnimation end="50" suffix="+" />
                    </h3>
                    <p className="stat-label">Projects Completed</p>
                  </div>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <div className="stat-item">
                    <h3 className="stat-number">
                      <CountUpAnimation end="5" suffix="+" />
                    </h3>
                    <p className="stat-label">Years Experience</p>
                  </div>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <div className="stat-item">
                    <h3 className="stat-number">
                      <CountUpAnimation end="100" suffix="+" />
                    </h3>
                    <p className="stat-label">Happy Clients</p>
                  </div>
                </Col>
                <Col md={3} sm={6} className="mb-3">
                  <div className="stat-item">
                    <h3 className="stat-number">24/7</h3>
                    <p className="stat-label">Support</p>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Container>
      </header>

      {/* 关于我部分 */}
      <section id="about" className="about-section">
        <GeometricBackground className="about-bg" />
        <Container>
          <h2 className="section-title text-center">About Me</h2>
          <Row className="align-items-center">
            <Col md={4} className="text-center mb-4 mb-md-0">
              <img 
                src={aboutImage} 
                className="img-fluid about-image" 
                alt="About Me" 
                style={{width: '250px', height: '250px', objectFit: 'cover'}} 
              />
            </Col>
            <Col md={8}>
              <div className="about-text">
                <p className="mb-4">
                  Hello! I'm a passionate full-stack developer with a love for creating innovative digital solutions. 
                  With over 5 years of experience in web development, I specialize in modern JavaScript frameworks, 
                  responsive design, and user experience optimization.
                </p>
                <p className="mb-4">
                  My journey in technology started with a curiosity about how things work, and it has evolved into 
                  a career dedicated to building applications that make a difference. I believe in clean code, 
                  continuous learning, and collaborative teamwork.
                </p>
                <p>
                  When I'm not coding, you can find me exploring new technologies, contributing to open-source projects, 
                  or enjoying outdoor adventures. Let's create something amazing together!
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 技能部分 */}
      <section id="skills" className="skills-section py-5">
        <GridBackground className="skills-bg" />
        <Container>
          <h2 className="section-title text-center">Skills & Technologies</h2>
          <Row className="text-center mb-5">
            {skillCards.map((skillCard, index) => (
              <Col md={3} className="mb-4" key={index}>
                <SkillCard 
                  icon={skillCard.icon}
                  title={skillCard.title}
                  description={skillCard.description}
                  skills={skillCard.skills}
                />
              </Col>
            ))}
          </Row>

          {/* 技能进度条 */}
          <Row>
            <Col lg={8} className="mx-auto">
              <h4 className="mb-4 text-center">Technical Proficiency</h4>
              {skills.map((skill, index) => (
                <div key={index} className="skill-progress-item mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="skill-name">{skill.name}</span>
                    <Badge bg="primary" className="skill-category-badge">{skill.category}</Badge>
                  </div>
                  <ProgressBar 
                    now={skill.level} 
                    label={`${skill.level}%`}
                    className="custom-progress"
                    variant="primary"
                  />
                </div>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      {/* 作品集部分 */}
      <section id="portfolio" className="portfolio-section">
        <LiquidBackground className="portfolio-bg" />
        <Container>
          <h2 className="section-title text-center">My Portfolio</h2>
          
          {/* 项目过滤器 */}
          <div className="text-center mb-5">
            <ProjectFilter 
              categories={projectCategories}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />
          </div>

          <Row>
            {filteredProjects.map((project) => (
              <Col lg={4} md={6} className="mb-4" key={project.id}>
                <ProjectCard 
                  project={project}
                  onViewDetails={openProjectModal}
                />
              </Col>
            ))}
          </Row>

          {filteredProjects.length === 0 && (
            <div className="text-center py-5">
              <h4>No projects found in this category</h4>
              <p>Try selecting a different category to see more projects.</p>
            </div>
          )}
        </Container>
      </section>

      {/* 联系部分 */}
      <section id="contact" className="contact-section py-5">
        <StarfieldBackground className="contact-bg" />
        <WaveBackground className="contact-waves" colors={['#667eea', '#764ba2']} />
        <Container className="position-relative">
          <h2 className="section-title text-center text-white">Get In Touch</h2>
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <p className="lead text-white mb-4">
                Have a project in mind? Let's discuss how we can bring your ideas to life.
              </p>
              <Button variant="outline-light" size="lg" className="me-3" onClick={() => setShowContactModal(true)}>
                Send Message
              </Button>
              <Button variant="light" size="lg" onClick={handleDownloadResume}>
                Download Resume
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 页脚 */}
      <footer className="footer text-center">
        <MatrixRainBackground className="footer-bg" />
        <Container className="position-relative">
          <Row>
            <Col md={12}>
              <h5 className="mb-3">Let's Connect</h5>
              <p className="mb-3">Ready to bring your ideas to life? Let's work together!</p>
              <div className="mb-3">
                <Button variant="outline-light" className="me-2" onClick={() => window.open('https://linkedin.com/in/yourprofile', '_blank')}>
                  LinkedIn
                </Button>
                <Button variant="outline-light" className="me-2" onClick={() => window.open('https://github.com/yourusername', '_blank')}>
                  GitHub
                </Button>
                <Button variant="outline-light" className="me-2" onClick={() => setShowContactModal(true)}>
                  Email
                </Button>
              </div>
              <hr className="my-4" />
              <p className="mb-0">&copy; 2025 My Portfolio. Crafted with love and lots of coffee.</p>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* 成功提醒 */}
      {showAlert && (
        <Alert variant="success" className="fixed-alert">
          {alertMessage}
        </Alert>
      )}

      {/* 联系表单模态框 */}
      <Modal show={showContactModal} onHide={() => setShowContactModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Get In Touch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleContactSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={contactForm.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your full name"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={contactForm.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                type="text"
                name="subject"
                value={contactForm.subject}
                onChange={handleInputChange}
                required
                placeholder="What's this about?"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Message *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="message"
                value={contactForm.message}
                onChange={handleInputChange}
                required
                placeholder="Tell me about your project or idea..."
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowContactModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Send Message
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* 项目详情模态框 */}
      <Modal show={showProjectModal} onHide={() => setShowProjectModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{selectedProject?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProject && (
            <Row>
              <Col md={6}>
                <img 
                  src={selectedProject.image} 
                  alt={selectedProject.title}
                  className="img-fluid rounded mb-3"
                  style={{width: '100%', height: '300px', objectFit: 'cover'}}
                />
                <div className="mb-3">
                  <h6>Technologies Used:</h6>
                  <div className="d-flex flex-wrap">
                    {selectedProject.technologies.map((tech, index) => (
                      <span key={index} className="badge bg-primary me-2 mb-2">{tech}</span>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <Button 
                    variant="outline-primary" 
                    className="me-2"
                    onClick={() => window.open(selectedProject.liveUrl, '_blank')}
                  >
                    Live Demo
                  </Button>
                  <Button 
                    variant="outline-secondary"
                    onClick={() => window.open(selectedProject.githubUrl, '_blank')}
                  >
                    View Code
                  </Button>
                </div>
              </Col>
              <Col md={6}>
                <h5>Project Description</h5>
                <p className="mb-4">{selectedProject.fullDesc}</p>
                
                <h6>Key Features:</h6>
                <ul className="list-unstyled">
                  {selectedProject.features.map((feature, index) => (
                    <li key={index} className="mb-2">
                      <i className="text-success me-2">✓</i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>

      {/* 简历下载模态框 */}
      <Modal show={showResumeModal} onHide={() => setShowResumeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Download Resume</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-4">
              <h5>My Professional Resume</h5>
              <p className="text-muted">
                Download my latest resume to learn more about my experience, skills, and achievements.
              </p>
            </div>
            <div className="d-grid gap-2">
              <Button 
                variant="primary" 
                size="lg"
                onClick={() => {
                  alert('Resume download would start here. Please add your actual resume file.');
                  setShowResumeModal(false);
                }}
              >
                Download PDF Resume
              </Button>
              <Button 
                variant="outline-secondary"
                onClick={() => setShowResumeModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default App;